import imageCompression from 'browser-image-compression';

/**
 * Compress image file before upload
 * @param {File} file - Image file to compress
 * @param {Object} customOptions - Optional custom compression options
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, customOptions = {}) {
  const defaultOptions = {
    maxSizeMB: 1,              // Maximum file size in MB
    maxWidthOrHeight: 1920,    // Maximum dimension (Full HD)
    useWebWorker: true,        // Use web worker for better performance
    fileType: 'image/jpeg',    // Convert to JPEG for better compression
    initialQuality: 0.8,       // Initial quality (0-1)
  };

  const options = { ...defaultOptions, ...customOptions };

  try {
    console.log('Original file size:', (file.size / 1024 / 1024).toFixed(2), 'MB');

    const compressedFile = await imageCompression(file, options);

    console.log('Compressed file size:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');
    console.log('Compression ratio:', ((1 - compressedFile.size / file.size) * 100).toFixed(1), '%');

    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    throw new Error('Nie udało się skompresować obrazu');
  }
}

/**
 * Convert base64 string to File object
 * @param {string} base64String - Base64 encoded image string
 * @param {string} fileName - Name for the file
 * @returns {File} - File object
 */
export function base64ToFile(base64String, fileName = 'image.jpg') {
  // Extract base64 data and mime type
  const matches = base64String.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);

  if (!matches || matches.length !== 3) {
    throw new Error('Invalid base64 string');
  }

  const mimeType = matches[1];
  const base64Data = matches[2];

  // Convert base64 to blob
  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    byteArrays.push(new Uint8Array(byteNumbers));
  }

  const blob = new Blob(byteArrays, { type: mimeType });

  // Convert blob to File
  return new File([blob], fileName, { type: mimeType });
}

/**
 * Validate image file
 * @param {File} file - File to validate
 * @param {Object} constraints - Validation constraints
 * @returns {Object} - { valid: boolean, error: string }
 */
export function validateImageFile(file, constraints = {}) {
  const {
    maxSizeMB = 10,
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  } = constraints;

  // Check if file exists
  if (!file) {
    return { valid: false, error: 'Nie wybrano pliku' };
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Nieprawidłowy format. Dozwolone: ${allowedTypes.join(', ')}`
    };
  }

  // Check file size
  const fileSizeMB = file.size / 1024 / 1024;
  if (fileSizeMB > maxSizeMB) {
    return {
      valid: false,
      error: `Plik jest za duży. Maksymalny rozmiar: ${maxSizeMB}MB`
    };
  }

  return { valid: true, error: null };
}

/**
 * Generate thumbnail from image file
 * @param {File} file - Image file
 * @param {number} maxDimension - Maximum width/height for thumbnail
 * @returns {Promise<File>} - Thumbnail file
 */
export async function generateThumbnail(file, maxDimension = 300) {
  return await compressImage(file, {
    maxSizeMB: 0.1,           // Very small for thumbnails
    maxWidthOrHeight: maxDimension,
    initialQuality: 0.7,
  });
}
